// Chwyty gitarowe do diagramów: nazwa akordu z utworu -> pozycje palców.
//
// Dane pochodzą z tombatossals/chords-db (MIT, `LICENSE-chords-db.txt` obok),
// przycięte skryptem `tools/chwyty-z-chords-db.mjs` do trzech pozycji na akord.
// Ten moduł nie rysuje niczego (to robi `render/diagram.ts`) i nie zna
// `Utwor` ani `Chart`: dostaje nazwę akordu, oddaje pozycje albo `null`.

import dane from './chwyty-dane.json';

/** Jedna pozycja chwytu w zapisie chords-db. `progi[i]` dotyczy struny od
 * najgrubszej (E2) do najcieńszej (E4): -1 = struna tłumiona, 0 = pusta,
 * n = próg liczony OD `progBazowy` (1 = pierwszy rysowany próg). */
export interface PozycjaChwytu {
  progi: readonly number[];
  palce: readonly number[];
  progBazowy: number;
  /** Progi (względne, jak w `progi`) przyciśnięte barré. */
  barre: readonly number[];
}

export interface Chwyt {
  /** Nazwa tak, jak stoi w utworze. */
  nazwa: string;
  pozycje: readonly PozycjaChwytu[];
  /** Bas z akordu ukośnikowego, gdy baza nie ma osobnego chwytu z tym basem:
   * pokazujemy wtedy chwyt bez basu i dopisek, a nie zmyślony układ. */
  basBezChwytu: string | null;
}

type Surowe = Record<string, Record<string, [number[], number[], number, number[]][]>>;
const BAZA = dane as unknown as Surowe;

/** Pisownia tonacji w chords-db. Enharmonie sprowadzamy do jednej nazwy;
 * polskie/niemieckie `H` to angielskie `B`. */
const TONACJE: Record<string, string> = {
  C: 'C', 'B#': 'C', 'C#': 'C#', Db: 'C#', D: 'D', 'D#': 'Eb', Eb: 'Eb',
  E: 'E', Fb: 'E', F: 'F', 'E#': 'F', 'F#': 'F#', Gb: 'F#', G: 'G',
  'G#': 'Ab', Ab: 'Ab', A: 'A', 'A#': 'Bb', Bb: 'Bb', B: 'B', Cb: 'B', H: 'B',
};

/** Pisownia basu w sufiksach ukośnikowych chords-db (`/F#`, `/Bb`...). */
const BASY: Record<string, string> = { ...TONACJE, 'D#': 'Eb', 'G#': 'G#', Ab: 'Ab', 'C#': 'C#' };

/** Zapisy sufiksu spotykane w tabach, sprowadzone do nazw chords-db. */
const SUFIKSY: Record<string, string> = {
  '': 'major', maj: 'major', M: 'major',
  m: 'minor', min: 'minor', mi: 'minor', '-': 'minor',
  M7: 'maj7', ma7: 'maj7', 'Δ': 'maj7', 'Δ7': 'maj7', j7: 'maj7',
  '+': 'aug', o: 'dim', '°': 'dim', o7: 'dim7', '°7': 'dim7',
  'ø': 'm7b5', 'ø7': 'm7b5', 'm7-5': 'm7b5', 'm7(b5)': 'm7b5',
  add2: 'add9', madd2: 'madd9', '7sus': '7sus4',
  min7: 'm7', mi7: 'm7', '-7': 'm7', min6: 'm6',
};

const WZOR = /^([A-H])([#b]?)([^/]*)(?:\/([A-H])([#b]?))?$/;

function pozycje(tonacja: string, sufiks: string): PozycjaChwytu[] | null {
  const surowe = BAZA[tonacja]?.[sufiks];
  if (!surowe || surowe.length === 0) return null;
  return surowe.map(([progi, palce, progBazowy, barre]) => ({ progi, palce, progBazowy, barre }));
}

/**
 * Szuka chwytu dla `nazwa` (np. `Am`, `D/F#`, `Hm7`). Zwraca `null`, gdy
 * nazwa się nie parsuje albo baza nie zna takiego akordu: diagramu nie
 * zgadujemy, bo zły chwyt jest gorszy niż jego brak.
 */
export function znajdzChwyt(nazwa: string): Chwyt | null {
  const dopasowanie = WZOR.exec(nazwa.trim());
  if (!dopasowanie) return null;
  const [, litera, znak, sufiksSurowy, basLitera, basZnak] = dopasowanie;
  const tonacja = TONACJE[`${litera}${znak}`];
  if (!tonacja) return null;
  const sufiks = SUFIKSY[sufiksSurowy ?? ''] ?? sufiksSurowy ?? '';

  if (basLitera) {
    const bas = BASY[`${basLitera}${basZnak ?? ''}`];
    if (!bas) return null;
    // chords-db trzyma ukośniki tylko dla trybu durowego i molowego.
    const kluczUkosny = sufiks === 'major' ? `/${bas}` : sufiks === 'minor' ? `m/${bas}` : null;
    const zBasem = kluczUkosny ? pozycje(tonacja, kluczUkosny) : null;
    if (zBasem) return { nazwa, pozycje: zBasem, basBezChwytu: null };
    const bezBasu = pozycje(tonacja, sufiks);
    return bezBasu ? { nazwa, pozycje: bezBasu, basBezChwytu: `${basLitera}${basZnak ?? ''}` } : null;
  }

  const znalezione = pozycje(tonacja, sufiks);
  return znalezione ? { nazwa, pozycje: znalezione, basBezChwytu: null } : null;
}

/** Unikalne nazwy akordów w kolejności pierwszego wystąpienia. Tokeny
 * nie-akordowe (`N.C.`, `x2`) odpadają, bo nie przejdą `WZOR`. */
export function unikalneAkordy(tokeny: Iterable<string>): string[] {
  const widziane = new Set<string>();
  for (const token of tokeny) {
    const t = token.trim();
    if (t !== '' && WZOR.test(t)) widziane.add(t);
  }
  return [...widziane];
}
